import { useMemo } from 'react';
import { css } from '@emotion/react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { OpsType } from '@weave/schema-gen-ts/dist/shared/organization/ops_type.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AiReceptionistOrg } from '@wetags/ai-receptionist';
import { AIRButton } from '@frontend/ai-receptionist-ui';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useGlobalPanel } from '@frontend/panel-engine';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { useTooltip } from '@frontend/design-system';
import { AIRSalesDemoTrackingIds } from '../tracking-ids';

export const AIRSalesDemoHeaderTrigger = () => {
  const [wetagEnabled] = AiReceptionistOrg.useWetag('air-sales-demo-header-trigger');
  const { t } = useTranslation('ai-receptionist');
  const { open } = useGlobalPanel('air-sales-demo');
  const { isSmallMode } = useIsSmallMode();
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({ placement: 'bottom' });

  const { accessibleLocationData, selectedLocationIds } = useAppScopeStore();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();

  const hasVoiceAIRAccess = useMemo(() => {
    return getCustomizationFlagValue(Feature.TRUELARK_AI_VOICE, State.ACTIVE, 'ALL');
  }, [getCustomizationFlagValue]);

  const { aggregateValue: hasAirCallSimulatorPermission } = checkAggregateACL(
    selectedLocationIds,
    Permission.AIR_CALL_SIMULATOR,
    'all'
  );

  const hasDemoLocation = useMemo(() => {
    return selectedLocationIds.every((id) => {
      const opsType = accessibleLocationData[id]?.opsType;
      return !!opsType && opsType === OpsType.DEMO;
    });
  }, [accessibleLocationData, selectedLocationIds]);

  const showTriggerButton = wetagEnabled && hasVoiceAIRAccess && hasAirCallSimulatorPermission && hasDemoLocation;

  if (!showTriggerButton) {
    return null;
  }

  const buttonProps = isSmallMode
    ? {
        ...triggerProps,
        iconOnly: true,
      }
    : {};

  return (
    <div css={styles.container}>
      <AIRButton
        {...buttonProps}
        onClick={open}
        trackingId={AIRSalesDemoTrackingIds.salesDemoHeaderButton}
        css={styles.button}
      >
        {t('AI Receptionist Demo')}
      </AIRButton>
      {isSmallMode && <Tooltip {...tooltipProps}>{t('AI Receptionist Demo')}</Tooltip>}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    height: 100%;
    padding: var(--fabric-space-gap-xs);
  `,
  button: css`
    max-width: 240px;
  `,
};
